import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '../analytics';
import '../PortfolioV2.css';

const services = [
  ['Créer un site', 'Présenter votre activité, rassurer vos visiteurs et leur permettre de vous contacter ou d’acheter.', '/services/creation-site-web'],
  ['Simplifier votre activité', 'Faire prendre rendez-vous à vos clients directement sur votre site, recevoir leurs demandes et simplifier les paiements et votre gestion.', '/services/reservation-paiement-backoffice'],
  ['Reprendre un projet', 'Projet inachevé, bugs ou mise en ligne bloquée. Je pars de l’existant avant de décider ce qui doit réellement être refait.', '/services/reprise-projet-web'],
];

const examples = [
  ['L’Atelier d’Héloïse', 'Boutique, rendez-vous pour les prestations, paiements et interface de gestion dans une plateforme sur mesure.', 'https://www.atelierdheloise.fr/'],
  ['SAMASS', 'Site de massages où les clients choisissent leur créneau et reçoivent une confirmation.', 'https://samassbysam.com/'],
  ['StockScan', 'Outil métier conçu pour gérer inventaires, stock, exports et opérations terrain.', 'https://stockscan.app/'],
];

export default function DeveloppeurWebQuimper() {
  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content');
    document.title = 'Création de site internet à Quimper | Alioune Seck';
    description?.setAttribute('content', 'Création de site internet à Quimper : sites sur mesure, prise de rendez-vous clients sur votre site et outils de gestion adaptés. Devis clair, travail à distance ou sur place.');
    trackEvent('acquisition_page_view', { page_type: 'local', location: 'quimper' });
    return () => {
      document.title = previousTitle;
      if (description && previousDescription) description.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="portfolio-v2 acquisition-page-v2">
      <header className="estimator-page-header">
        <Link className="v2-brand" to="/">
          <span className="v2-brand-symbol">AS</span>
          <span><strong>Alioune Seck</strong><small>Quimper · Finistère</small></span>
        </Link>
        <Link className="v2-header-cta" to="/estimer-mon-projet" onClick={() => trackEvent('cta_click', { placement: 'local_header', target: 'estimator' })}>Estimer mon projet ↗</Link>
      </header>

      <main className="acquisition-main">
        <section className="acquisition-hero acquisition-local-hero">
          <span className="v2-kicker">Quimper · Finistère · À distance</span>
          <h1>Création de site internet à Quimper, et outils web sur mesure.</h1>
          <p>Développeur web freelance basé à Quimper, je crée des sites internet et des outils pour les indépendants et petites entreprises. Présenter votre activité, permettre à vos clients de prendre rendez-vous sur votre site ou simplifier votre gestion : on part de votre besoin, pas d’une solution toute faite.</p>
          <div className="v2-hero-actions">
            <Link className="v2-primary" to="/estimer-mon-projet" onClick={() => trackEvent('cta_click', { placement: 'local_hero', target: 'estimator' })}>Estimer mon projet →</Link>
            <Link className="v2-secondary" to="/#contact">Me parler du besoin</Link>
          </div>
        </section>

        <section className="acquisition-local-intro">
          <span className="v2-kicker">Travailler localement</span>
          <h2>Un interlocuteur à Quimper, sans le fonctionnement d’une grosse agence.</h2>
          <p>Je peux vous rencontrer à Quimper lorsque c’est utile et travailler à distance pour le reste du projet. Vous n’avez pas besoin de connaître le vocabulaire technique ni d’arriver avec un cahier des charges complet.</p>
        </section>

        <section className="acquisition-service-grid">
          {services.map(([title, text, href], index) => (
            <article key={title}>
              <span>0{index + 1}</span>
              <h2>{title}</h2>
              <p>{text}</p>
              <Link className="v2-inline-cta" to={href}>Voir ce besoin →</Link>
            </article>
          ))}
        </section>

        <section className="acquisition-work-proof">
          <div className="v2-section-heading">
            <span className="v2-kicker">Réalisations</span>
            <h2>Des projets qui répondent à des besoins différents.</h2>
            <p>Pas une seule maquette répétée. Chaque projet part de l’activité, du public et du problème à résoudre.</p>
          </div>
          <div className="acquisition-proof-grid">
            {examples.map(([title, text, href]) => (
              <a key={title} href={href} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('local_project_click', { project: title })}>
                <strong>{title}</strong>
                <p>{text}</p>
                <span>Ouvrir le projet ↗</span>
              </a>
            ))}
          </div>
        </section>

        <section className="acquisition-budget">
          <div>
            <span className="v2-kicker">Repères de budget</span>
            <h2>850 € à 1 250 € pour un site vitrine simple. À partir de 1 250 € pour ajouter la prise de rendez-vous clients.</h2>
          </div>
          <p>Les fonctions comme paiement, espace client, back-office, connexion à un logiciel ou import de données font évoluer le périmètre. Le simulateur permet de les tester sans jargon.</p>
        </section>

        <section className="acquisition-local-intro acquisition-faq">
          <span className="v2-kicker">Questions fréquentes</span>
          <h2>Combien coûte la création d’un site internet à Quimper ?</h2>
          <p>Un site vitrine simple se situe généralement entre 850 € et 1 250 € dans mes prestations. Le tarif évolue si vos clients doivent pouvoir prendre rendez-vous sur votre site, payer une prestation, acheter un produit ou si vous avez besoin d’un espace de gestion. <Link to="/pricing">Voir les repères de tarifs →</Link></p>
          <h2>Faut-il se rencontrer pour lancer le projet ?</h2>
          <p>Non. Nous pouvons cadrer le besoin, suivre les étapes et valider le résultat à distance. Un rendez-vous à Quimper reste possible si cela facilite le projet.</p>
          <h2>Et si vous avez déjà un site ou un projet commencé ?</h2>
          <p>Je peux aussi examiner un site existant, identifier les points bloquants et proposer une reprise ciblée plutôt qu’une refonte systématique. <Link to="/services/reprise-projet-web">Découvrir la reprise de projet →</Link></p>
        </section>

        <section className="acquisition-convert">
          <span className="v2-kicker">Votre projet</span>
          <h2>Quelques minutes pour savoir ce qui serait réellement utile.</h2>
          <p>Choisissez vos besoins, voyez ce qui peut attendre et obtenez un premier ordre de grandeur du budget et du délai.</p>
          <Link className="v2-primary" to="/estimer-mon-projet" onClick={() => trackEvent('cta_click', { placement: 'local_bottom', target: 'estimator' })}>Faire une estimation →</Link>
        </section>
      </main>
    </div>
  );
}
